'use client';

import { Fragment, ReactNode, useState } from 'react';
import {
  Placement,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import { Popover, Portal, Transition } from '@headlessui/react';

import { cn } from '@/utils/uiUtils';

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  placement?: Placement;
  trigger?: 'hover' | 'click';
  className?: string;
}

export function Tooltip({
  children,
  content,
  placement = 'top',
  trigger = 'hover',
  className,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const {
    refs: { setReference, setFloating },
    floatingStyles,
  } = useFloating({
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift()],
    open: trigger === 'hover' ? open : undefined,
  });

  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      setOpen(false);
    }
  };

  if (trigger === 'click') {
    return (
      <Popover className={cn('relative', className)}>
        {({ open: popoverOpen }) => (
          <>
            <Popover.Button as={Fragment} ref={setReference}>
              {children}
            </Popover.Button>
            <Portal>
              <div ref={setFloating} style={floatingStyles} className='z-[100]'>
                <Transition
                  as={Fragment}
                  show={popoverOpen}
                  enter='transition ease-out duration-200'
                  enterFrom='opacity-0 translate-y-1'
                  enterTo='opacity-100 translate-y-0'
                  leave='transition ease-in duration-150'
                  leaveFrom='opacity-100 translate-y-0'
                  leaveTo='opacity-0 translate-y-1'
                >
                  <Popover.Panel
                    static
                    className='rounded-lg border border-stone-300 bg-white p-2 text-sm text-gray-900 shadow-lg dark:border-stone-700 dark:bg-stone-700 dark:text-gray-100'
                  >
                    {content}
                  </Popover.Panel>
                </Transition>
              </div>
            </Portal>
          </>
        )}
      </Popover>
    );
  }

  // Hover trigger
  return (
    <div
      className={cn('relative', className)}
      ref={setReference}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <Portal>
        {open && (
          <div
            ref={setFloating}
            style={floatingStyles}
            className='z-[100]'
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Transition
              as={Fragment}
              show={open}
              enter='transition ease-out duration-200'
              enterFrom='opacity-0 translate-y-1'
              enterTo='opacity-100 translate-y-0'
              leave='transition ease-in duration-150'
              leaveFrom='opacity-100 translate-y-0'
              leaveTo='opacity-0 translate-y-1'
            >
              <div className='rounded-lg border border-stone-300 bg-white p-2 text-sm text-gray-900 shadow-lg dark:border-stone-700 dark:bg-stone-700 dark:text-gray-100'>
                {content}
              </div>
            </Transition>
          </div>
        )}
      </Portal>
    </div>
  );
}
